"use client";

import React, { useState, useMemo, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  RefreshCw,
  Copy,
  Download,
  Sparkles,
  Search,
  FileText,
  FileCode,
  FileSpreadsheet,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import NameCard from "@/components/generator/NameCard";
import LoreStoryModal from "@/components/generator/LoreStoryModal";
import { generateLoreProfile, LoreProfile } from "@/lib/loreGenerator";
import { toast } from "sonner";
import { imageForSlug } from "@/lib/categoryImages";
import { getCategoryUrl } from "@/lib/categoryUrls";
import { useAudioFeedback } from "@/hooks/useAudioFeedback";
import { useFavorites } from "@/hooks/useFavorites";

export interface CategoryData {
  type: string;
  name: string;
  slug: string;
  icon?: string;
  title: string;
  description: string;
  names: Array<string | { name: string; meaning?: string }>;
  parent?: string | null;
  parentSlug?: string | null;
  relatedSubcategories?: Array<{ name: string; slug: string }>;
  /** Full query-matching title ("Weequay Name Generator") for the on-page H1.
   * Falls back to `name` (bare entity, e.g. "Weequay") when not provided. */
  generatorTitle?: string;
}

type StyleFilter = "all" | "noble" | "dark" | "mystic" | "short";

export default function CategoryGeneratorClient({
  initialData,
  slug,
}: {
  initialData: CategoryData | null;
  slug: string;
}) {
  const router = useRouter();
  const { playPop, playChime } = useAudioFeedback();
  const { isFavorite, addFavorite, removeFavorite, favorites } = useFavorites();

  // Derived straight from the prop -- it was previously seeded into state that
  // nothing ever updated, which went stale if the component was reused across a
  // client-side navigation to another generator.
  const data = initialData;

  const [items, setItems] = useState<Array<string | { name: string; meaning?: string }>>(
    initialData?.names || []
  );
  const [displayCount, setDisplayCount] = useState<number>(12);
  const [filterQuery, setFilterQuery] = useState("");
  const [styleFilter, setStyleFilter] = useState<StyleFilter>("all");
  const [genderFilter, setGenderFilter] = useState<"all" | "Male" | "Female" | "Unisex">("all");
  const [religionFilter, setReligionFilter] = useState<"all" | "Muslim" | "Hindu">("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedLoreProfile, setSelectedLoreProfile] = useState<LoreProfile | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Keep the working list in sync if this instance is reused for another slug.
  useEffect(() => {
    setItems(initialData?.names || []);
    setDisplayCount(12);
    setFilterQuery("");
    setStyleFilter("all");
    setGenderFilter("all");
    setReligionFilter("all");
  }, [slug, initialData]);

  const hasMuslim = useMemo(() => {
    return items.some((item) => {
      if (typeof item !== "object" || !item) return false;
      const r = (item as any).religion || "";
      const m = item.meaning || "";
      return r.toLowerCase() === "muslim" || /\[muslim/i.test(m);
    });
  }, [items]);

  const hasHindu = useMemo(() => {
    return items.some((item) => {
      if (typeof item !== "object" || !item) return false;
      const r = (item as any).religion || "";
      const m = item.meaning || "";
      return r.toLowerCase() === "hindu" || /\[hindu/i.test(m);
    });
  }, [items]);

  const hasReligion = hasMuslim || hasHindu;

  const hasGender = useMemo(() => {
    return items.some((item) => {
      if (typeof item !== "object" || !item) return false;
      const m = item.meaning || "";
      return /(Male|Female|Unisex)/i.test(m) || !!(item as any).gender;
    });
  }, [items]);

  const hasUnisex = useMemo(() => {
    return items.some((item) => {
      if (typeof item !== "object" || !item) return false;
      const g = (item as any).gender || "";
      return g.toLowerCase() === "unisex";
    });
  }, [items]);

  // Shuffle array helper
  const shuffleNames = (list: any[]) => {
    const arr = [...list];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const handleReroll = () => {
    setIsGenerating(true);
    playPop();
    setTimeout(() => {
      if (data && data.names) {
        setItems(shuffleNames(data.names));
      }
      setIsGenerating(false);
      playChime();
      toast.success("Forged fresh names from the realm! ✨");
    }, 220);
  };

  // Lucky Name Discovery
  const handleLuckyPick = () => {
    if (!items.length) return;
    playPop();
    const randomIndex = Math.floor(Math.random() * items.length);
    const chosen = items[randomIndex];
    const nameStr = typeof chosen === "string" ? chosen : chosen?.name || "";
    const meanStr = typeof chosen === "object" && chosen?.meaning ? chosen.meaning : undefined;
    
    playChime();
    toast.success(`✨ Discovered: ${nameStr}!`);
    handleInspectLore(nameStr, meanStr);
  };

  const handleInspectLore = (name: string, meaning?: string) => {
    playPop();
    const profile = generateLoreProfile(name, meaning, data?.name);
    setSelectedLoreProfile(profile);
  };

  const visibleItems = useMemo(() => {
    let result = items;

    // Demographic: Gender Filter
    if (genderFilter !== "all") {
      result = result.filter((item) => {
        if (typeof item === "object" && item) {
          if ((item as any).gender) return (item as any).gender.toLowerCase() === genderFilter.toLowerCase();
          const m = item.meaning || "";
          return m.toLowerCase().includes(` ${genderFilter.toLowerCase()}]`) || m.toLowerCase().includes(`[${genderFilter.toLowerCase()}`);
        }
        return false;
      });
    }

    // Demographic: Religion / Tradition Filter
    if (religionFilter !== "all") {
      result = result.filter((item) => {
        if (typeof item === "object" && item) {
          if ((item as any).religion) return (item as any).religion.toLowerCase() === religionFilter.toLowerCase();
          const m = item.meaning || "";
          return m.toLowerCase().includes(`[${religionFilter.toLowerCase()}`);
        }
        return false;
      });
    }

    // Style tone filtering
    if (styleFilter !== "all") {
      result = result.filter((item) => {
        const nameStr = typeof item === "string" ? item : item?.name || "";
        const meanStr = typeof item === "object" && item?.meaning ? item.meaning : "";
        const combined = `${nameStr} ${meanStr}`.toLowerCase();

        if (styleFilter === "short") {
          return !nameStr.includes(" ") && nameStr.length <= 8;
        }
        if (styleFilter === "noble") {
          return /sovereign|royal|high|king|queen|lord|lady|noble|court|throne|pure/i.test(combined);
        }
        if (styleFilter === "dark") {
          return /shadow|dark|abyss|curse|dread|gloom|void|eclipse|blood|night/i.test(combined);
        }
        if (styleFilter === "mystic") {
          return /star|celestial|arcane|rune|lunar|divine|magic|spell|spirit|light/i.test(combined);
        }
        return true;
      });
    }

    // Text search query filter
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter((item) => {
        const nameStr = typeof item === "string" ? item : item?.name || "";
        const meanStr = typeof item === "object" && item?.meaning ? item.meaning : "";
        return nameStr.toLowerCase().includes(q) || meanStr.toLowerCase().includes(q);
      });
    }

    return result.slice(0, displayCount);
  }, [items, filterQuery, styleFilter, genderFilter, religionFilter, displayCount]);

  // Export handlers
  const handleCopyAll = async () => {
    const text = visibleItems
      .map((item) => {
        if (typeof item === "string") return item;
        return item.meaning ? `${item.name} - ${item.meaning}` : item.name;
      })
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      playChime();
      toast.success("Copied names to clipboard!");
    } catch {
      toast.error("Failed to copy names");
    }
  };

  const handleExportMarkdown = () => {
    let md = `# ${data?.name || "Names"} Collection\n\n`;
    md += `| # | Name | Lore Meaning & Origin |\n`;
    md += `| :---: | :--- | :--- |\n`;
    visibleItems.forEach((item, i) => {
      const n = typeof item === "string" ? item : item.name;
      const m = typeof item === "object" && item.meaning ? item.meaning : "-";
      md += `| ${i + 1} | **${n}** | ${m} |\n`;
    });
    md += `\n*Generated via Magical Name Generator*\n`;

    downloadBlob(md, `${slug}-names.md`, "text/markdown");
    toast.success("Exported Markdown (.md) table for Notion & Obsidian!");
  };

  const handleExportCSV = () => {
    let csv = `Index,Name,Meaning\n`;
    visibleItems.forEach((item, i) => {
      const n = typeof item === "string" ? item : item.name;
      const m = typeof item === "object" && item.meaning ? `"${item.meaning.replace(/"/g, '""')}"` : `""`;
      csv += `${i + 1},"${n}",${m}\n`;
    });

    downloadBlob(csv, `${slug}-names.csv`, "text/csv");
    toast.success("Exported CSV spreadsheet for game development!");
  };

  const handleExportJSON = () => {
    const json = JSON.stringify(
      {
        generator: data?.name,
        slug: slug,
        totalExported: visibleItems.length,
        names: visibleItems.map((item) => {
          if (typeof item === "string") return { name: item };
          return { name: item.name, meaning: item.meaning };
        }),
      },
      null,
      2
    );

    downloadBlob(json, `${slug}-names.json`, "application/json");
    toast.success("Exported JSON dataset!");
  };

  const handleExportTXT = () => {
    const content = `${data?.name || "Names"} Generator: Magical Name Generator\n\n` +
      visibleItems
        .map((item, i) => {
          if (typeof item === "string") return `${i + 1}. ${item}`;
          return `${i + 1}. ${item.name}${item.meaning ? ` - ${item.meaning}` : ""}`;
        })
        .join("\n");

    downloadBlob(content, `${slug}-names.txt`, "text/plain");
    toast.success("Downloaded plain text file!");
  };

  const downloadBlob = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    // Firefox only honours a synthetic click on an anchor that is in the
    // document, and revoking the object URL synchronously races the download in
    // Firefox and Safari. Append, click, then clean up on the next tick.
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 0);
    setShowExportMenu(false);
  };

  if (!data) {
    return (
      <div className="min-h-[70vh] pt-32 pb-16 px-4 flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center border-border/80">
          <div className="text-4xl mb-4">🔮</div>
          <h2 className="text-2xl font-bold font-fantasy text-foreground mb-2">
            Realm Not Found
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            We couldn&apos;t find a naming generator for &ldquo;{slug}&rdquo;.
          </p>
          <Link href="/">
            <Button variant="fantasy" className="w-full">
              Return to All Realms
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Lore Story Modal */}
      <LoreStoryModal
        profile={selectedLoreProfile}
        isOpen={!!selectedLoreProfile}
        onClose={() => setSelectedLoreProfile(null)}
        onFavorite={(n, m) => {
          if (isFavorite(n)) {
            const match = favorites.find((f) => f.name === n);
            if (match) removeFavorite(match.id);
            else removeFavorite(n);
          } else {
            addFavorite(n, data.name, m);
          }
        }}
        isFavorite={selectedLoreProfile ? isFavorite(selectedLoreProfile.name) : false}
      />

      {/* Breadcrumb / Back Navigation */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => router.back()}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>

        {data.parent && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link
              href={getCategoryUrl(data.parentSlug || data.parent)}
              className="hover:text-primary transition-colors"
            >
              {data.parent}
            </Link>
            <span>/</span>
            <span className="text-foreground font-semibold">{data.name}</span>
          </div>
        )}
      </div>

      {/* Hero Atmosphere Banner */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative mb-8 rounded-3xl overflow-hidden border border-border/80 bg-card shadow-xl"
      >
        {/* Shorter on mobile so the actual generator (search bar, filters,
            first names) sits closer to the fold -- audit found the name list
            starting 1.6+ screen-heights down on a 375px viewport. */}
        <div className="relative h-28 sm:h-64 w-full overflow-hidden">
          <Image
            src={imageForSlug(data.parentSlug || data.slug, data.slug)}
            alt={`${data.name} visual atmosphere`}
            fill
            priority
            sizes="(max-width: 1200px) 100vw, 1200px"
            className="object-cover brightness-[0.75] dark:brightness-[0.6] transition-transform duration-700 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-card/90 via-card/40 to-transparent" />
        </div>

        <div className="relative -mt-10 sm:-mt-24 px-6 sm:px-8 pb-4 sm:pb-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-background/90 backdrop-blur-md border border-border shadow-xl flex items-center justify-center text-3xl sm:text-4xl">
                {data.icon || "✨"}
              </div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold font-fantasy text-foreground tracking-tight drop-shadow-sm">
                  {data.generatorTitle || data.name}
                </h1>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <Badge variant="fantasy" className="text-xs shadow-sm">
                    Unique Lore Names
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Collapsed on mobile so the generator itself isn't pushed below
              the fold by the lore paragraph; fully visible from sm: up. */}
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-4xl line-clamp-2 sm:line-clamp-none">
            {data.description}
          </p>
        </div>
      </motion.div>

      {/* Style / Tone Filters Pills */}
      <div className="mb-4 flex flex-col gap-2.5">
        {hasMuslim && hasHindu && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-2xl bg-muted/30 border border-border/60 text-xs">
            {/* Quick Combined Presets */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-muted-foreground font-semibold shrink-0 mr-1">
                Tradition & Gender:
              </span>
              {[
                { label: "All Traditions", religion: "all", gender: "all" },
                { label: "🌙 Muslim Male", religion: "Muslim", gender: "Male" },
                { label: "🌙 Muslim Female", religion: "Muslim", gender: "Female" },
                { label: "🕉️ Hindu Male", religion: "Hindu", gender: "Male" },
                { label: "🕉️ Hindu Female", religion: "Hindu", gender: "Female" },
              ].map((preset) => {
                const active = religionFilter === preset.religion && genderFilter === preset.gender;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setReligionFilter(preset.religion as any);
                      setGenderFilter(preset.gender as any);
                    }}
                    className={`px-3 py-1.5 rounded-xl font-medium transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30"
                        : "bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background border border-border/50"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {hasMuslim && !hasHindu && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-2xl bg-muted/30 border border-border/60 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-muted-foreground font-semibold shrink-0 mr-1">
                Gender:
              </span>
              {[
                { label: "All Names", gender: "all" },
                { label: "🌙 ♂ Muslim Male", gender: "Male" },
                { label: "🌙 ♀ Muslim Female", gender: "Female" },
              ].map((preset) => {
                const active = genderFilter === preset.gender;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setReligionFilter("all");
                      setGenderFilter(preset.gender as any);
                    }}
                    className={`px-3 py-1.5 rounded-xl font-medium transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30"
                        : "bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background border border-border/50"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!hasMuslim && hasHindu && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-2xl bg-muted/30 border border-border/60 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-muted-foreground font-semibold shrink-0 mr-1">
                Gender:
              </span>
              {[
                { label: "All Names", gender: "all" },
                { label: "🕉️ ♂ Hindu Male", gender: "Male" },
                { label: "🕉️ ♀ Hindu Female", gender: "Female" },
              ].map((preset) => {
                const active = genderFilter === preset.gender;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setReligionFilter("all");
                      setGenderFilter(preset.gender as any);
                    }}
                    className={`px-3 py-1.5 rounded-xl font-medium transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30"
                        : "bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background border border-border/50"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!hasReligion && hasGender && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-2xl bg-muted/30 border border-border/60 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-muted-foreground font-semibold shrink-0 mr-1">
                Gender:
              </span>
              {[
                { label: "All Names", gender: "all" },
                { label: "♂ Male Names", gender: "Male" },
                { label: "♀ Female Names", gender: "Female" },
                ...(hasUnisex ? [{ label: "⚧ Unisex", gender: "Unisex" }] : []),
              ].map((preset) => {
                const active = genderFilter === preset.gender;
                return (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setGenderFilter(preset.gender as any);
                    }}
                    className={`px-3 py-1.5 rounded-xl font-medium transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30"
                        : "bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background border border-border/50"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="relative">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
            <span className="text-muted-foreground font-semibold flex items-center gap-1 shrink-0 mr-1">
              <Filter className="w-3.5 h-3.5" />
              Filter Tone:
            </span>
            {[
              { id: "all", label: "All Names" },
              { id: "noble", label: "👑 Noble & Royal" },
              { id: "dark", label: "🌑 Dark & Shadow" },
              { id: "mystic", label: "✨ Mystic & Arcane" },
              { id: "short", label: "⚡ Short (1-Word)" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStyleFilter(tab.id as StyleFilter)}
                className={`px-3 py-1.5 rounded-xl font-medium transition-all shrink-0 ${
                  styleFilter === tab.id
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Trailing fade so a horizontally-scrollable row doesn't look like
              a dead end when the last chip is cut off mid-word. */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-background to-transparent" />
        </div>
      </div>

      {/* Generator Controls Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative z-30 mb-8 p-4 rounded-2xl border border-border/80 bg-card/90 backdrop-blur-md shadow-sm"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              type="text"
              placeholder="Search names or meanings..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="pl-9 h-10 text-xs rounded-xl"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end flex-wrap">
            {/* Lucky Pick Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLuckyPick}
              title="Discover a random name and inspect lore"
              className="rounded-xl h-10 gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
            >
              <Sparkles className="w-4 h-4" />
              <span>Lucky Pick</span>
            </Button>

            {/* Generate Fresh Button */}
            <Button
              variant="fantasy"
              size="sm"
              onClick={handleReroll}
              disabled={isGenerating}
              className="rounded-xl h-10 gap-1.5 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`} />
              <span>Generate Fresh</span>
            </Button>

            {/* Copy Visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyAll}
              className="rounded-xl h-10 gap-1.5 text-xs"
            >
              <Copy className="w-4 h-4" />
              <span>Copy All</span>
            </Button>

            {/* Export Multi-Format Menu */}
            <div className="relative z-40">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="rounded-xl h-10 gap-1.5 text-xs"
              >
                <Download className="w-4 h-4" />
                <span>Export Tools</span>
              </Button>

              <AnimatePresence>
                {showExportMenu && (
                  <>
                    {/* Backdrop click dismiss */}
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowExportMenu(false)}
                    />

                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-60 rounded-2xl border border-border/90 bg-[#12110f] text-foreground p-2 shadow-[0_20px_50px_rgba(0,0,0,0.85)] z-50 space-y-1 ring-1 ring-white/10"
                    >
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 border-b border-border/40 mb-1">
                        Export Format
                      </div>

                      <button
                        onClick={handleExportMarkdown}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors text-left group"
                      >
                        <div className="h-7 w-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 text-primary group-hover:scale-105 transition-transform">
                          <FileText className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">Markdown Table</div>
                          <div className="text-[10px] text-muted-foreground">Obsidian &amp; Notion ready</div>
                        </div>
                      </button>

                      <button
                        onClick={handleExportCSV}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors text-left group"
                      >
                        <div className="h-7 w-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 text-emerald-400 group-hover:scale-105 transition-transform">
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">CSV Spreadsheet</div>
                          <div className="text-[10px] text-muted-foreground">Game dev &amp; Excel sheets</div>
                        </div>
                      </button>

                      <button
                        onClick={handleExportJSON}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors text-left group"
                      >
                        <div className="h-7 w-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 text-amber-400 group-hover:scale-105 transition-transform">
                          <FileCode className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">JSON Object</div>
                          <div className="text-[10px] text-muted-foreground">App &amp; API datasets</div>
                        </div>
                      </button>

                      <button
                        onClick={handleExportTXT}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors text-left group"
                      >
                        <div className="h-7 w-7 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 text-blue-400 group-hover:scale-105 transition-transform">
                          <Download className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">Plain Text (.txt)</div>
                          <div className="text-[10px] text-muted-foreground">Simple numbered list</div>
                        </div>
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Names Grid */}
      <div className="space-y-3 mb-10">
        <AnimatePresence mode="popLayout">
          {visibleItems.length > 0 ? (
            visibleItems.map((item, index) => (
              <NameCard
                key={typeof item === "string" ? `${item}-${index}` : `${item.name}-${index}`}
                name={item}
                category={data.name}
                index={index}
                onInspectLore={handleInspectLore}
              />
            ))
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-sm">
                No names matched your filter &ldquo;{filterQuery}&rdquo;.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilterQuery("");
                  setStyleFilter("all");
                }}
                className="mt-3 rounded-xl"
              >
                Reset All Filters
              </Button>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Load More Button */}
      {displayCount < items.length && !filterQuery && styleFilter === "all" && (
        <div className="text-center mb-16">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setDisplayCount((prev) => prev + 12)}
            className="rounded-2xl px-8 border-primary/30 hover:border-primary shadow-sm"
          >
            Show More
          </Button>
        </div>
      )}
    </div>
  );
}
